import { SolicitarElementoNoListado } from "@/features/solicitudes-catalogo/components/SolicitarElementoNoListado";
import { CodigoConNombre } from "./CodigoConNombre";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { useCecosParaSolicitud, useCuentasParaSolicitud, useIndicadoresImpuesto } from "@/features/catalogos/hooks/use-catalogos";
import { ClaveSatPicker } from "@/features/catalogos/components/ClaveSatPicker";
import { useEditarLineaManual } from "@/features/prorrateo/hooks/use-prorrateo-mutations";
import { useSociedadDeProrrateo } from "./ProrrateoSociedadContext";
import type { LineaManualInput, LineaProrrateo } from "@/features/prorrateo/types";
import { Badge, Group, IconAction, ImporteInput, Input, Select, Td, Text, Tr } from "@/components/ui";
import { useMonedaDeProrrateo } from "./ProrrateoSociedadContext";

interface Props {
  /** Invoice FB60: el indicador lo determina el proveedor del Invoice (solo lectura). */
  indicadorFijo?: boolean;
  linea: LineaProrrateo;
  solicitudId: string;
  onClose: () => void;
}

/**
 * Fila en modo edición · única dispatch: si la línea es automática,
 * permite solo cambiar centro de costo + importe (decisión 16 del
 * dominio). Si es manual, todos los campos son editables.
 *
 * El estado local del form es minimalista (5 campos sin validación
 * cruzada, controlado a mano) · al confirmar dispara la mutation
 * correspondiente y cierra al éxito.
 */
export function LineaRowEdit({ linea, solicitudId, onClose, indicadorFijo = false }: Props) {
  const moneda = useMonedaDeProrrateo();
  const sociedad = useSociedadDeProrrateo();
  const { data: cecos } = useCecosParaSolicitud(sociedad);
  const { data: cuentas } = useCuentasParaSolicitud(sociedad);
  const { data: indicadores } = useIndicadoresImpuesto();
  const esManual = linea.tipo === "Manual";

  const [form, setForm] = useState<LineaManualInput>({
    claveSat: linea.claveSat,
    descripcion: linea.descripcion,
    centroDeCostoId: linea.centroDeCostoId,
    cuentaContableId: linea.cuentaContableId,
    importe: linea.importe,
    indicador: linea.indicador,
  });
  const set = <K extends keyof LineaManualInput>(k: K, v: LineaManualInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  const editManual = useEditarLineaManual(solicitudId);
  const saving = editManual.isPending;

  const guardar = async () => {
    await editManual.mutateAsync({ lineaId: linea.id, input: form });
    onClose();
  };

  // RN-15 · el catálogo activo completo, más el código actual de la línea si
  // ya fue dado de baja (para no dejar el Select en blanco al editar una
  // línea histórica con un indicador desactivado).
  const indicadorOptions = indicadores.some((i) => i.codigo === form.indicador)
    ? indicadores
    : [...indicadores, { id: form.indicador, codigo: form.indicador, tasaOCuota: 0, descripcion: "(dado de baja)", activo: false }];

  return (
    <Tr className="bg-muted/50">
      <Td className="font-mono">{linea.numeroLinea}</Td>
      <Td>
        <Badge variant="light" color={esManual ? "azulProfundo" : "info"} uppercase={false}>
          {linea.tipo}
        </Badge>
      </Td>
      <Td>
        {esManual ? (
          <ClaveSatPicker size="xs" value={form.claveSat} onChange={(v) => set("claveSat", v)} className="w-30" />
        ) : (
          <Text size="xs" ff="mono">
            {linea.claveSat}
          </Text>
        )}
      </Td>
      <Td>{esManual ? <Input size="xs" value={form.descripcion} onChange={(e) => set("descripcion", e.currentTarget.value)} /> : <Text size="sm">{linea.descripcion}</Text>}</Td>
      <Td>
        <div className="flex items-center gap-1">
        <Select
          size="xs"
          options={cecos.map((c) => ({ value: c.id, label: `${c.codigo} · ${c.nombre}` }))}
          value={form.centroDeCostoId}
          onValueChange={(v) => set("centroDeCostoId", v)}
          searchable
          className="w-45"
        />
        <SolicitarElementoNoListado tipo="CentroDeCosto" numeroSociedad={sociedad} solicitudPagoId={solicitudId} />
        </div>
      </Td>
      <Td>
        <div className="flex items-center gap-1">
        {esManual ? (
          <Select
            size="xs"
            options={cuentas.map((c) => ({ value: c.id, label: `${c.codigo} · ${c.nombre}` }))}
            value={form.cuentaContableId}
            onValueChange={(v) => set("cuentaContableId", v)}
            searchable
            className="w-45"
          />
        ) : null}
        {esManual ? (
          <SolicitarElementoNoListado tipo="CuentaContable" numeroSociedad={sociedad} solicitudPagoId={solicitudId} />
        ) : (
          <CodigoConNombre codigo={linea.cuentaContableCodigo} nombre={linea.cuentaContableNombre} />
        )}
        </div>
      </Td>
      <Td align="right">
        <ImporteInput
          size="xs"
          value={form.importe}
          moneda={moneda}
          onChange={(v) => set("importe", typeof v === "number" ? v : 0)}
          className="w-36"
          wrapperClassName="ml-auto"
        />
      </Td>
      <Td>
        {indicadorFijo ? (
          <Badge variant="light" color="slate" uppercase={false}>
            {linea.indicador}
          </Badge>
        ) : (
          <Select
            size="xs"
            options={indicadorOptions.map((i) => ({ value: i.codigo, label: `${i.codigo} · ${(i.tasaOCuota * 100).toFixed(0)}%` }))}
            value={form.indicador}
            onValueChange={(v) => set("indicador", v)}
            className="w-27"
          />
        )}
      </Td>
      <Td align="right">
        <Group gap={4} justify="flex-end" wrap="nowrap">
          <IconAction label="Guardar" variant="filled" size="sm" loading={saving} onClick={guardar}>
            <IconCheck size={14} stroke={1.8} />
          </IconAction>
          <IconAction label="Cancelar" variant="subtle" size="sm" onClick={onClose}>
            <IconX size={14} stroke={1.7} />
          </IconAction>
        </Group>
      </Td>
    </Tr>
  );
}
